
import React from 'react';
import { useDispatch  } from 'react-redux';
import { DefaultButton } from 'office-ui-fabric-react/lib/Button';
import FileSaver from 'file-saver';
import get from 'lodash/get';

import { DialogConfirm } from '../../../core';

import {
    useDataProjectId,
    useToken
} from '../../../project';

import {
    sagas as printSagas,
    usePrintDefinition
} from '../../../print';

import { calculateObjectKeyForDocument } from '../../reducers/document';
import { getActions } from '../../sagas';
import {
    useDataModelOperationUpdate
} from '../../hooks/useDataModelOperation';

import getAttributeValueAsText from '../getAttributeValueAsText';
import DialogDocumentLink from '../document/DialogDocumentLink';
import DialogEditAttribute from './DialogEditAttribute';

const ObjectListOperation = ({
    dataModelItem,
    selection
}) => {

    const REPORT_TYPE_DETAIL = 'report_detail';
    const REPORT_TYPE_LIST = 'report_list';

    const dispatch = useDispatch();
    const dataActions = getActions(dispatch);
    const printActions = printSagas.getActions(dispatch);

    const token = useToken();
    const projectId = useDataProjectId();
    const printDefinition = usePrintDefinition();
    const operationUpdate = useDataModelOperationUpdate(dataModelItem);

    const getGidsForObjects = () => {
        return selection.workObjectList.map(
            object => object.attributes['gid']
        );
    }

    const getReport = (reportType) => {
        let report;
        const listReportId = get(dataModelItem, `reports.${reportType}`);
        if (listReportId && printDefinition && printDefinition.reportDefinition) {
            report = printDefinition.reportDefinition.reduce((previous, current) => {
                return (current.id === listReportId)
                    ? current
                    : previous;
            }, null);
        }
        return report;
    }

    const handlePrint = (reportType) => {
        let report = getReport(reportType);
        if (report) {
            let params = report.params.reduce((previous, current) => {
                return {
                    ...previous,
                    [current.id]: (current.id !== 'gids')
                        ? current.defaultValue || ''
                        : getGidsForObjects().join(',')
                };
            }, {});
            const paramsForReport = JSON.stringify(params);

            printActions.generateReport(report, paramsForReport);
        }
    }

    const handlePrintDetail = async () => {
        let canPrint = true;
        if (selection.workObjectList.length > 100) {
            canPrint = await DialogConfirm.show(`Počet objektů pro tisk detailu je ${selection.workObjectList.length}. Opravdu chcete pokračovat v tisku ?`);
        }
        if (canPrint) {
            handlePrint(REPORT_TYPE_DETAIL);
        }
    }

    const handlePrintList = () => {
        handlePrint(REPORT_TYPE_LIST);
    }

    const handleEditAttribute = async () => {
        const attributes = dataModelItem.attributes.map(item => {
            return {
                id: item.name,
                ...item
            }
        }).filter(item => {
            return isAttributeAllowUpdate(item);
        });

        const dialog = DialogEditAttribute();
        let res = await dialog.open(attributes);
        if (res.button === 1) {
            dataActions.updateAttributeForObjects(
                token, projectId, dataModelItem.id, {
                    attribute: res.attribute,
                    value: res.value,
                    gids: getGidsForObjects()
                }
            )
        }
    }

    const handleAddDocumentList = async () => {
        let res = await DialogDocumentLink.show('', '', '');
        if (res) {
            dataActions.insertDocumentToObjects(
                token, projectId,
                {
                    objectKeys: selection.workObjectList.map(
                        object => calculateObjectKeyForDocument(object, dataModelItem.attachedDocuments)
                    ),
                    url: res.url,
                    title: res.title,
                    description: res.description
                }
            );
        }
    }

    const handleExportCsv = () => {
        let content = [
            dataModelItem.attributes.map(attr => attr.title).join(';'),
            ...selection.workObjectList.map(object => {
                return dataModelItem.attributes.map(attr => {
                    const value = object.attributes[attr.name];
                    let valueAsText = getAttributeValueAsText(attr, value);
                    if (!attr.values) {
                        switch (attr.type) {
                            case 'float':
                                valueAsText = value ? value.toString().replace('.', ',') : '';
                                break;
                            case 'integer':
                                valueAsText = value ? value.toString() : '';
                                break;
                            default:
                                // nop;
                        }
                    }
                    return valueAsText;
                }).join(';');
            })
        ].join('\r\n');

        let blob = new Blob(['\ufeff', content], {type: 'text/plain;charset=utf-8;'});
        FileSaver.saveAs(blob, `${dataModelItem.id}.csv`);

    }

    const isAttributeAllowUpdate = (attribute) => {
        let allow = !attribute.generated;
        if (operationUpdate.allowAttributes) {
            allow = allow && (operationUpdate.allowAttributes.indexOf(attribute.name) !== -1);
        }
        return allow
    }

    const isDisabled = () => {
        return (
            !selection ||
            (selection && selection.workObjectList.length === 0) ||
            !dataModelItem
        );
    }

    const isEditAttributeDisabled = () => {
        return !dataModelItem || !operationUpdate;
    }

    const isPrintDetailDisabled = () => {
        const report = getReport(REPORT_TYPE_DETAIL);
        return (!report);
    }

    const isPrintListDisabled = () => {
        const report = getReport(REPORT_TYPE_LIST);
        return (!report);
    }

    return (
        <DefaultButton
            text="Hromadné operace"
            disabled={isDisabled()}
            menuProps={{
                items: [{
                    disabled: isPrintDetailDisabled(),
                    key: 'printDetail',
                    text: 'Tisk detailu objektů',
                    title: 'Vygenerovat tiskovou sestavu obsahující detail pro objekty',
                    onClick: handlePrintDetail
                }, {
                    disabled: isPrintListDisabled(),
                    key: 'printList',
                    text: 'Tisk seznamu objektů',
                    title: 'Vygenerovat tiskovou sestavu obsahující seznam objektů',
                    onClick: handlePrintList
                }, {
                    disabled: isEditAttributeDisabled(),
                    key: 'editAttribute',
                    text: 'Přepis atributu',
                    title: 'Přepsat vybraný atribut zadanou u objektů',
                    onClick: handleEditAttribute
                }, {
                    disabled: (!dataModelItem || !dataModelItem.attachedDocuments),
                    key: 'addDocumentList',
                    text: 'Přidat odkaz na dokument',
                    title: 'Přidat odkaz na dokument objektům',
                    onClick: handleAddDocumentList
                }, {
                    key: 'exportCsv',
                    text: 'Export objektů do CSV',
                    title: 'Export objektů do CVS',
                    onClick: handleExportCsv
                }]
            }}
        />
    );
}

export default ObjectListOperation;
